import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';

/**
 * The single client read of "what plan is this company on".
 *
 * A company holds exactly one plan row (enforced in the database), so there is no
 * product/tier pair, no free floor and no synthetic fallback here. Access questions
 * still belong to useEntitlements — this hook is identity and status only.
 */

export type PlanState = {
  id: string;
  key: string;
  name: string;
  tagline: string | null;
  best_for: string | null;
  price_cents: number | null;
  currency: string | null;
  billing_interval: string | null;
  badge: string | null;
  status: string;
  current_period_end: string | null;
  is_pioneer: boolean;
  is_override: boolean;
  override_expires_at: string | null;
  override_reason: string | null;
  grid_revision: number | null;
  /** Billing lifecycle state from the subscription state machine. */
  settlement_state: string | null;
  dunning_attempts: number | null;
  grace_until: string | null;
  /** When non-null and in the future, the company is in a 60-day referral trial. */
  trial_ends_at: string | null;
};

type Payload = { company_id: string | null; plan: PlanState | null };

export function usePlanState() {
  const { user } = useAuth();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['my-plan', user?.id],
    queryFn: async (): Promise<Payload> => {
      const { data, error } = await supabase.rpc('get_my_plan');
      if (error) throw error;
      return (data as unknown as Payload) ?? { company_id: null, plan: null };
    },
    enabled: !!user,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });

  /* A failed read must never read as "no plan" — that would hide capabilities the
     company already holds. Callers render a retry, never an upsell. */
  return {
    plan: data?.plan ?? null,
    companyId: data?.company_id ?? null,
    isLoading,
    isError: !!error,
    error,
    refetch,
  };
}
